import React, { useEffect, useState } from 'react';
import Header from '../header';
import Footer from '../Footer';
import { Shield, TrendingUp, BarChart2, Target, Eye, Heart, Landmark, Briefcase, Network, User } from 'lucide-react';

const AboutUs = () => {
  const fullText = "Empowering Your \nFinancial Legacy";
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setTypedText(fullText.slice(0, index + 1));
      index++;
      if (index === fullText.length) clearInterval(interval);
    }, 70); // Typing speed
    return () => clearInterval(interval);
  }, []);

  const display1 = typedText.split('\n')[0] || '';
  const display2 = typedText.split('\n')[1] || '';

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30 overflow-x-hidden">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[url('https://res.cloudinary.com/dvqwan8qc/image/upload/v1769418002/nerusfull_tdovvh.jpg')] bg-cover bg-center opacity-5"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/20 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-cyan-400 text-sm font-bold uppercase tracking-widest mb-8">
            <Shield className="w-4 h-4" />
            <span>Established 2025</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-slate-500 tracking-tight mb-8 pb-4 min-h-[140px] md:min-h-[180px]">
            {display1} <br className="hidden md:block" /> {display2}
            {typedText.length < fullText.length && (
              <span 
                className="inline-block w-1.5 md:w-2.5 h-[0.8em] bg-cyan-400 ml-1 md:ml-3 align-baseline animate-pulse" 
                style={{ WebkitBackgroundClip: 'initial', backgroundClip: 'initial' }}
              />
            )}
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto font-medium leading-relaxed mb-12">
            Invest More. Grow More. Become Wealthier.
          </p>
        </div>
      </section>

      {/* Story & Origin Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-black text-white">The NERUS Story</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full"></div>

              <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
                <p>
                  NERUS GROUP was established in 2025 with a clear vision: to empower individuals and families to participate confidently in the financial markets and build lasting wealth for future generations.
                </p>
                <p>
                  The name <strong className="text-white">NERUS</strong> is inspired by ancient Greek origins, symbolizing strength, growth, and the pursuit of excellence. It reflects our commitment to guiding investors toward financial progress through knowledge, discipline, and strategic planning.
                </p>
                <p>
                  We believe that financial success is not simply about earning returns—it is about creating opportunities, achieving life goals, and building a legacy that can positively impact future generations. Our purpose is to help people transform their aspirations into reality through informed investing and disciplined financial decision-making.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-3xl transform rotate-3 scale-105 blur-lg"></div>
              <div className="relative bg-slate-900 border border-white/10 p-8 md:p-12 rounded-3xl backdrop-blur-xl">
                <Landmark className="w-16 h-16 text-cyan-400 mb-8" />
                <h3 className="text-2xl font-bold text-white mb-4">Active Participation</h3>
                <p className="text-slate-400 text-lg leading-relaxed mb-8">
                  At NERUS GROUP, we are committed to making investing more accessible, understandable, and meaningful. We encourage individuals to become active participants in wealth creation rather than passive observers of financial markets.
                </p>
                <div className="flex items-center gap-4 text-sm font-bold text-slate-300 uppercase tracking-widest border-t border-white/10 pt-8">
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-cyan-400"></div> Knowledge</div>
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Discipline</div>
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> Strategy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Specialized Companies */}
      <section className="py-24 bg-slate-900/50 relative border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">Our Ecosystem</h2>
            <p className="text-xl text-slate-400">
              NERUS GROUP serves as the parent organization of three specialized companies, providing a comprehensive ecosystem designed to support investors at every stage of their wealth creation journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-950 border border-white/5 hover:border-cyan-500/30 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 group">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <TrendingUp className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Nerus Wealth</h3>
              <p className="text-slate-400 leading-relaxed">
                Dedicated to comprehensive wealth management, portfolio structuring, and long-term asset growth for individuals and families.
              </p>
            </div>

            <div className="bg-slate-950 border border-white/5 hover:border-blue-500/30 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 group">
              <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Nerus Securities</h3>
              <p className="text-slate-400 leading-relaxed">
                Ensuring secure, compliant, and robust execution of financial transactions and asset protection across all markets.
              </p>
            </div>

            <div className="bg-slate-950 border border-white/5 hover:border-purple-500/30 p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2 group">
              <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <BarChart2 className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Nerus Trading & Advisory</h3>
              <p className="text-slate-400 leading-relaxed">
                Delivering actionable market intelligence, strategic trading advice, and algorithmic insights for active market participants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-24 relative overflow-hidden bg-slate-950">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">Our Leadership</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-slate-400">
              The visionary minds guiding NERUS GROUP towards a stronger financial future.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            {/* Leader 1 */}
            <div className="bg-white/5 border border-white/10 hover:border-cyan-500/50 p-6 rounded-3xl text-center transition-all duration-300 hover:-translate-y-2 group">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <User className="w-10 h-10 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Chukka Neerab Kumar</h3>
              <p className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-1">Co-Founder & CEO</p>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Chief Executive Officer</p>
            </div>

            {/* Leader 2 */}
            <div className="bg-white/5 border border-white/10 hover:border-blue-500/50 p-6 rounded-3xl text-center transition-all duration-300 hover:-translate-y-2 group">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500/20 to-indigo-600/20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <User className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Chukka Chakravarthy</h3>
              <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">Co-Founder & CFO</p>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Chief Financial Officer</p>
            </div>

            {/* Leader 3 */}
            <div className="bg-white/5 border border-white/10 hover:border-indigo-500/50 p-6 rounded-3xl text-center transition-all duration-300 hover:-translate-y-2 group">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <User className="w-10 h-10 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">KPS Sachin</h3>
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-1">CTO</p>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Chief Technology Officer</p>
            </div>

            {/* Leader 4 */}
            <div className="bg-white/5 border border-white/10 hover:border-purple-500/50 p-6 rounded-3xl text-center transition-all duration-300 hover:-translate-y-2 group">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <User className="w-10 h-10 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Sriram Vikas</h3>
              <p className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-1">CPO</p>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Chief Product Officer</p>
            </div>

          </div>
        </div>
      </section>

      {/* Vision, Mission, Philosophy */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute right-0 bottom-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Vision */}
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10 rounded-3xl relative overflow-hidden">
              <Eye className="absolute -right-6 -top-6 w-32 h-32 text-white/5" />
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-cyan-500/20 rounded-xl text-cyan-400">
                  <Eye className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-black text-white uppercase tracking-widest">Our Vision</h2>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed font-medium">
                To create a financially empowered society where every individual has the knowledge, confidence, and opportunity to build sustainable wealth and leave a lasting legacy for generations to come.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-bl from-white/5 to-transparent border border-white/10 p-10 rounded-3xl relative overflow-hidden">
              <Target className="absolute -right-6 -top-6 w-32 h-32 text-white/5" />
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-blue-500/20 rounded-xl text-blue-400">
                  <Target className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-black text-white uppercase tracking-widest">Our Mission</h2>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed font-medium">
                To provide trusted financial guidance, investment solutions, and wealth-building opportunities that help individuals and families achieve their goals through disciplined investing and long-term financial planning.
              </p>
            </div>

            {/* Philosophy (Full Width) */}
            <div className="md:col-span-2 bg-gradient-to-b from-slate-900 to-slate-950 border border-white/10 p-10 md:p-16 rounded-3xl text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50"></div>

              <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-widest mb-4">Our Philosophy</h2>
              <p className="text-3xl md:text-5xl font-black text-white mb-8 tracking-tight">
                Invest More. Grow More. <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Become Wealthier.</span>
              </p>
              <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed">
                Because wealth is not just about today's success—it is about creating opportunities, security, and prosperity for generations to come.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer Banner */}
      <section className="py-20 border-t border-white/10 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Heart className="w-12 h-12 text-cyan-400 mx-auto mb-6 animate-pulse" />
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6">NERUS GROUP</h2>
          <p className="text-2xl font-medium text-slate-400 uppercase tracking-widest">
            Your Trust. Our Commitment.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
